import { useState, memo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Star, Zap, Target, ChevronRight, ArrowUp, ArrowDown, Minus, Trophy, Gift } from 'lucide-react';
import PlayerCardModal from '@/components/PlayerCardModal';
import { calculateForm } from '@/lib/formCalculation';
import { getLevelFromStats } from '@/lib/levelSystem';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

interface SkillRatings {
  pace?: number;
  shooting?: number;
  passing?: number;
  dribbling?: number;
  defending?: number;
  physical?: number;
}

interface Player {
  id: string;
  name: string;
  slug: string;
  avatar: string | null;
  points: number;
  wins: number;
  goals: number;
  gameId?: string | null;
  matches?: number;
  draws?: number;
  losses?: number;
  rankChange?: number;
  previousRank?: number;
  position?: string | null;
  jersey_number?: number | null;
  overall_rating?: number | null;
  skill_ratings?: SkillRatings | null;
  goals_against?: number;
  skills_manual_override?: boolean;
  assists?: number;
}

interface TopPlayersShowcaseProps {
  players: Player[];
}

function TopPlayersShowcaseComponent({ players }: TopPlayersShowcaseProps) {
  const [selectedPlayer, setSelectedPlayer] = useState<{ player: Player; rank: number } | null>(null);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const top3 = players.slice(0, 3);
  
  const displaySlots: (Player | null)[] = [
    top3[0] || null,
    top3[1] || null,
    top3[2] || null,
  ];

  // Auto-play effect
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [api]);

  // Track current slide
  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Position-specific configurations
  const getPositionConfig = (index: number) => {
    switch (index) {
      case 0:
        return {
          badge: '1ST',
          badgeBg: 'bg-gradient-to-r from-amber-500 to-amber-600',
          ringColor: '#00D4FF', // Cyan
          glowColor: 'rgba(0, 212, 255, 0.6)',
          winRateColor: '#00D4FF',
          crownColor: 'text-cyan-400',
        };
      case 1:
        return {
          badge: '2ND',
          badgeBg: 'bg-gradient-to-r from-amber-700 to-amber-800',
          ringColor: '#FFD700', // Yellow/Gold
          glowColor: 'rgba(255, 215, 0, 0.6)',
          winRateColor: '#FFD700',
          crownColor: 'text-yellow-400',
        };
      case 2:
        return {
          badge: '3RD',
          badgeBg: 'bg-gradient-to-r from-orange-600 to-orange-700',
          ringColor: '#CD7F32', // Bronze
          glowColor: 'rgba(205, 127, 50, 0.6)',
          winRateColor: '#CD7F32',
          crownColor: 'text-orange-400',
        };
      default:
        return {
          badge: '',
          badgeBg: 'bg-gray-600',
          ringColor: '#888',
          glowColor: 'rgba(136, 136, 136, 0.5)',
          winRateColor: '#888',
          crownColor: 'text-gray-400',
        };
    }
  };

  const calculateWinRate = (player: Player) => {
    const totalMatches = (player.wins || 0) + (player.draws || 0) + (player.losses || 0);
    if (totalMatches === 0) return 0;
    return Math.round((player.wins / totalMatches) * 100);
  };

  const getRankChangeDisplay = (player: Player) => {
    if (player.rankChange === undefined) {
      return { icon: Star, text: 'NEW', color: 'text-amber-400' };
    }
    if (player.rankChange > 0) {
      return { icon: ArrowUp, text: `+${player.rankChange}`, color: 'text-green-400' };
    }
    if (player.rankChange < 0) {
      return { icon: ArrowDown, text: `${player.rankChange}`, color: 'text-red-400' };
    }
    return { icon: Minus, text: '0', color: 'text-gray-400' };
  };

  const getOVRColor = (ovr: number) => {
    if (ovr >= 85) return 'bg-gradient-to-b from-yellow-400 to-amber-500';
    if (ovr >= 75) return 'bg-gradient-to-b from-lime-400 to-green-500';
    if (ovr >= 65) return 'bg-gradient-to-b from-blue-400 to-blue-600';
    return 'bg-gradient-to-b from-gray-400 to-gray-600';
  };

  const getFormColor = (grade: string) => {
    if (grade === 'S' || grade === 'A') return 'bg-gradient-to-b from-green-400 to-green-600';
    if (grade === 'B') return 'bg-gradient-to-b from-lime-400 to-lime-600';
    if (grade === 'C') return 'bg-gradient-to-b from-yellow-400 to-yellow-600';
    return 'bg-gradient-to-b from-red-400 to-red-600';
  };

  const renderCard = (player: Player | null, actualPosition: number) => {
    const config = getPositionConfig(actualPosition);

    // Placeholder card
    if (!player) {
      return (
        <div className="w-full px-2">
          <div className="bg-card/50 rounded-2xl border border-dashed border-muted-foreground/30 p-6 min-h-[480px] flex flex-col items-center justify-center">
            <Crown className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground/60 font-medium mb-2">Awaiting Champion</p>
            <Link 
              to="/auth" 
              className="text-primary text-sm hover:underline flex items-center gap-1"
            >
              Join Now <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      );
    }

    const winRate = calculateWinRate(player);
    const totalMatches = (player.wins || 0) + (player.draws || 0) + (player.losses || 0);
    const rankChangeInfo = getRankChangeDisplay(player);
    const RankIcon = rankChangeInfo.icon;
    const ovr = player.overall_rating || 60;
    const playerStats = {
      matches: totalMatches,
      wins: player.wins || 0,
      draws: player.draws || 0,
      losses: player.losses || 0,
      goals: player.goals || 0,
      assists: player.assists || 0,
      points: player.points || 0,
    };
    const formData = calculateForm(playerStats);
    const playerLevel = getLevelFromStats(playerStats);
    
    const skills = player.skill_ratings || {};
    const pace = skills.pace || 50;
    const shooting = skills.shooting || 50;
    const passing = skills.passing || 50;
    const dribbling = skills.dribbling || 50;
    const defending = skills.defending || 50;
    const physical = skills.physical || 50;

    return (
      <div className="w-full px-2">
        <div 
          onClick={() => setSelectedPlayer({ player, rank: actualPosition + 1 })}
          className="relative bg-card/90 rounded-2xl overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
        >
          {/* Glowing Corner Borders */}
          <div className="absolute top-0 left-0 w-12 h-12">
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
            <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
          </div>
          <div className="absolute top-0 right-0 w-12 h-12">
            <div className="absolute top-0 right-0 w-full h-[3px] bg-gradient-to-l from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
            <div className="absolute top-0 right-0 h-full w-[3px] bg-gradient-to-b from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
          </div>
          <div className="absolute bottom-0 left-0 w-12 h-12">
            <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
            <div className="absolute bottom-0 left-0 h-full w-[3px] bg-gradient-to-t from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
          </div>
          <div className="absolute bottom-0 right-0 w-12 h-12">
            <div className="absolute bottom-0 right-0 w-full h-[3px] bg-gradient-to-l from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
            <div className="absolute bottom-0 right-0 h-full w-[3px] bg-gradient-to-t from-cyan-400 to-transparent" style={{ boxShadow: `0 0 10px ${config.glowColor}` }} />
          </div>

          {/* Position Badge */}
          <div className="absolute top-2 right-2 z-10">
            <div className={`${config.badgeBg} px-3 py-1 rounded-lg`}>
              <span className="text-white text-sm font-bold">{config.badge}</span>
            </div>
          </div>

          <div className="p-4 pt-3">
            {/* Top Row - OVR & FORM */}
            <div className="flex justify-center gap-3 mb-3">
              {/* OVR Badge */}
              <div className={`${getOVRColor(ovr)} rounded-lg px-4 py-1 text-center min-w-[50px]`}>
                <span className="text-2xl font-black text-black">{ovr}</span>
                <p className="text-[10px] font-bold text-black/80 -mt-1">OVR</p>
              </div>
              
              {/* FORM Badge */}
              <div className={`${getFormColor(formData.grade)} rounded-lg px-4 py-1 text-center min-w-[50px]`}>
                <span className="text-2xl font-black text-black">{formData.grade}</span>
                <p className="text-[10px] font-bold text-black/80 -mt-1">FORM</p>
              </div>
            </div>

            {/* Level, NEW, Gift Row */}
            <div className="flex justify-center gap-2 mb-4">
              {/* Level Badge */}
              <div className="bg-gray-800 border border-gray-600 rounded-full px-3 py-1 flex items-center gap-1">
                <span className="text-orange-500">🔥</span>
                <span className="text-white text-sm font-bold">Lv.{playerLevel.level}</span>
              </div>
              
              {/* NEW Badge */}
              <div className="bg-cyan-500/20 border border-cyan-400 rounded-full px-3 py-1 flex items-center gap-1.5">
                <RankIcon className={`h-3 w-3 ${rankChangeInfo.color}`} />
                <span className={`text-sm font-bold ${rankChangeInfo.color}`}>{rankChangeInfo.text}</span>
              </div>
              
              {/* Gift Icon */}
              <div className="bg-gray-800 border border-gray-600 rounded-full p-1.5">
                <Gift className="h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Avatar Section */}
            <div className="flex gap-4 items-start">
              {/* Left Side - Avatar & Info */}
              <div className="flex-shrink-0 text-center">
                {/* Crown */}
                <Crown className={`h-5 w-5 mx-auto mb-1 ${config.crownColor}`} />
                
                {/* Avatar with Ring */}
                <div className="relative w-24 h-24 mx-auto mb-2">
                  {/* Win Rate Ring */}
                  <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
                    <circle
                      cx="50" cy="50" r="46" fill="none"
                      stroke={config.ringColor}
                      strokeWidth="4" strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 46}
                      strokeDashoffset={2 * Math.PI * 46 - (winRate / 100) * 2 * Math.PI * 46}
                      style={{ filter: `drop-shadow(0 0 6px ${config.ringColor})` }}
                    />
                  </svg>
                  
                  {/* Avatar */}
                  <div className="absolute inset-2 rounded-full overflow-hidden border-2" style={{ borderColor: config.ringColor }}>
                    {player.avatar ? (
                      <img src={player.avatar} alt={player.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-muted flex items-center justify-center">
                        <span className="text-2xl font-bold text-foreground">{player.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Player Name */}
                <h3 className="font-bold text-foreground text-sm mb-0.5 truncate max-w-[140px]">{player.name}</h3>
                
                {/* Game ID */}
                {player.gameId && (
                  <p className="text-xs text-cyan-400 font-mono truncate max-w-[140px]">@{player.gameId}</p>
                )}

                {/* Win Rate Bar */}
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] mb-1">
                    <span className="text-muted-foreground">Win Rate</span>
                    <span className="font-bold" style={{ color: config.winRateColor }}>{winRate}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${winRate}%`, 
                        backgroundColor: config.winRateColor,
                        boxShadow: `0 0 8px ${config.winRateColor}`
                      }} 
                    />
                  </div>
                </div>
              </div>

              {/* Right Side - Stats */}
              <div className="flex-1">
                {/* Main Stats Grid */}
                <div className="bg-card border border-border/50 rounded-xl p-3 mb-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center">
                      <Star className="h-4 w-4 mx-auto mb-0.5 text-muted-foreground" />
                      <p className="text-xl font-bold text-foreground">{player.points}</p>
                      <p className="text-[10px] text-muted-foreground">PTS</p>
                    </div>
                    <div className="text-center">
                      <Trophy className="h-4 w-4 mx-auto mb-0.5 text-muted-foreground" />
                      <p className="text-xl font-bold text-foreground">{player.wins}</p>
                      <p className="text-[10px] text-muted-foreground">WINS</p>
                    </div>
                    <div className="text-center">
                      <Zap className="h-4 w-4 mx-auto mb-0.5 text-muted-foreground" />
                      <p className="text-xl font-bold text-foreground">{player.goals}</p>
                      <p className="text-[10px] text-muted-foreground">GOALS</p>
                    </div>
                    <div className="text-center">
                      <Target className="h-4 w-4 mx-auto mb-0.5 text-muted-foreground" />
                      <p className="text-xl font-bold text-foreground">{totalMatches}</p>
                      <p className="text-[10px] text-muted-foreground">MATCH</p>
                    </div>
                  </div>
                </div>

                {/* Skills Row */}
                <div className="bg-card border border-border/50 rounded-xl p-2">
                  <div className="grid grid-cols-6 gap-1 text-center">
                    {[
                      { label: 'PAC', value: pace },
                      { label: 'SHO', value: shooting },
                      { label: 'PAS', value: passing },
                      { label: 'DRI', value: dribbling },
                      { label: 'DEF', value: defending },
                      { label: 'PHY', value: physical },
                    ].map((skill) => (
                      <div key={skill.label}>
                        <p className={`text-sm font-bold ${
                          skill.value >= 90 ? 'text-green-400' :
                          skill.value >= 80 ? 'text-lime-400' :
                          skill.value >= 70 ? 'text-yellow-400' :
                          skill.value >= 60 ? 'text-orange-400' :
                          'text-red-400'
                        }`}>{skill.value}</p>
                        <p className="text-[8px] text-muted-foreground">{skill.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Tap for details */}
            <div className="text-center mt-3">
              <span className="text-[10px] text-muted-foreground flex items-center justify-center gap-1">
                👆 Tap for details
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="py-8">
      <div className="container">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Crown className="h-5 w-5 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Top Champions</h2>
          <p className="text-sm text-muted-foreground mt-1">Elite players dominating the leaderboard</p>
        </div>

        {/* Carousel */}
        <div className="relative max-w-sm mx-auto">
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent>
              {displaySlots.map((player, index) => (
                <CarouselItem key={player?.id || `empty-${index}`}>
                  {renderCard(player, index)}
                </CarouselItem>
              ))}
            </CarouselContent>
            
            <CarouselPrevious className="left-0 -translate-x-1/2 h-8 w-8 bg-card/80 border-border hover:bg-muted" />
            <CarouselNext className="right-0 translate-x-1/2 h-8 w-8 bg-card/80 border-border hover:bg-muted" />
          </Carousel>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {displaySlots.map((_, index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  current === index 
                    ? 'w-6 bg-primary' 
                    : 'w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>
        </div>

        {/* View All Link */}
        <div className="text-center mt-6">
          <Link 
            to="/leaderboard" 
            className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
          >
            View Full Leaderboard
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Player Card Modal */}
      {selectedPlayer && (
        <PlayerCardModal
          player={{
            id: selectedPlayer.player.id,
            name: selectedPlayer.player.name,
            slug: selectedPlayer.player.slug,
            avatar: selectedPlayer.player.avatar,
            position: selectedPlayer.player.position,
            jersey_number: selectedPlayer.player.jersey_number,
            overall_rating: selectedPlayer.player.overall_rating,
            skill_ratings: selectedPlayer.player.skill_ratings,
          }}
          leaderboardEntry={{
            matches_played: (selectedPlayer.player.wins || 0) + (selectedPlayer.player.draws || 0) + (selectedPlayer.player.losses || 0),
            wins: selectedPlayer.player.wins || 0,
            draws: selectedPlayer.player.draws || 0,
            losses: selectedPlayer.player.losses || 0,
            goals_for: selectedPlayer.player.goals || 0,
            goals_against: selectedPlayer.player.goals_against || 0,
            points: selectedPlayer.player.points || 0,
          }}
          rank={selectedPlayer.rank}
          isOpen={true}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </section>
  );
}

export const TopPlayersShowcase = memo(TopPlayersShowcaseComponent);
